import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Space,
  Popconfirm,
  Switch,
  Tag,
  Badge,
  Tooltip
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  TrophyOutlined,
  FilterOutlined,
  LockOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Option } = Select;
const { TextArea } = Input;

interface Achievement {
  id: number;
  code: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  condition_type: string;
  condition_value: number;
  condition_params: string;
  rarity: string;
  sort_order: number;
  is_active: boolean;
  is_hidden: boolean;
  reward_points: number;
  reward_coupon_code: string;
}

const AchievementMasterList: React.FC = () => {
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAchievement, setEditingAchievement] = useState<Achievement | null>(null);
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterRarity, setFilterRarity] = useState<string>('all');

  const [form] = Form.useForm();

  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

  // TODO: 将来的にfeature flagやDB設定でアンロック判定に切り替え
  const isUnlocked = true;

  useEffect(() => {
    if (isUnlocked) fetchAchievements();
  }, []);

  const fetchAchievements = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/achievements/master`);
      setAchievements(response.data.achievements || response.data);
    } catch (error) {
      message.error('称号マスタの取得に失敗しました');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      if (editingAchievement) {
        await axios.put(`${API_URL}/achievements/master/${editingAchievement.id}`, values);
        message.success('称号を更新しました');
      } else {
        await axios.post(`${API_URL}/achievements/master`, values);
        message.success('称号を作成しました');
      }
      setModalVisible(false);
      form.resetFields();
      setEditingAchievement(null);
      fetchAchievements();
    } catch (error: any) {
      message.error(error.response?.data?.error || '操作に失敗しました');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    setLoading(true);
    try {
      await axios.delete(`${API_URL}/achievements/master/${id}`);
      message.success('称号を削除しました');
      fetchAchievements();
    } catch (error: any) {
      message.error(error.response?.data?.error || '称号の削除に失敗しました');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (id: number, is_active: boolean) => {
    setLoading(true);
    try {
      await axios.put(`${API_URL}/achievements/master/${id}/toggle`, { is_active });
      message.success(is_active ? '称号を有効化しました' : '称号を無効化しました');
      fetchAchievements();
    } catch (error: any) {
      message.error('操作に失敗しました');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (achievement: Achievement) => {
    setEditingAchievement(achievement);
    form.setFieldsValue(achievement);
    setModalVisible(true);
  };

  const openCreateModal = () => {
    setEditingAchievement(null);
    form.resetFields();
    setModalVisible(true);
  };

  // フィルタリング
  const filteredAchievements = achievements.filter(ach => {
    if (filterCategory !== 'all' && ach.category !== filterCategory) return false;
    if (filterRarity !== 'all' && ach.rarity !== filterRarity) return false;
    return true;
  });

  const categoryOptions = [
    { value: 'all', label: '全カテゴリ' },
    { value: 'visit', label: '🏪 来店系' },
    { value: 'purchase', label: '💰 購入系' },
    { value: 'point', label: '💎 ポイント系' },
    { value: 'menu', label: '🍴 メニュー系' },
    { value: 'special', label: '⭐ 特別系' }
  ];

  const rarityOptions = [
    { value: 'all', label: '全レアリティ' },
    { value: 'common', label: 'コモン' },
    { value: 'rare', label: 'レア' },
    { value: 'epic', label: 'エピック' },
    { value: 'legendary', label: 'レジェンダリー' }
  ];

  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'common': return 'default';
      case 'rare': return 'blue';
      case 'epic': return 'purple';
      case 'legendary': return 'gold';
      default: return 'default';
    }
  };

  const columns = [
    {
      title: '並び順',
      dataIndex: 'sort_order',
      key: 'sort_order',
      width: 80,
      sorter: (a: Achievement, b: Achievement) => a.sort_order - b.sort_order
    },
    {
      title: 'アイコン',
      dataIndex: 'icon',
      key: 'icon',
      width: 60,
      render: (icon: string) => <span style={{ fontSize: '24px' }}>{icon}</span>
    },
    {
      title: 'コード',
      dataIndex: 'code',
      key: 'code',
      width: 150,
      ellipsis: true
    },
    {
      title: '称号名',
      dataIndex: 'name',
      key: 'name',
      width: 150
    },
    {
      title: '説明',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    },
    {
      title: 'カテゴリ',
      dataIndex: 'category',
      key: 'category',
      width: 100,
      render: (category: string) => {
        const categoryMap: any = {
          visit: '🏪 来店',
          purchase: '💰 購入',
          point: '💎 ポイント',
          menu: '🍴 メニュー',
          special: '⭐ 特別'
        };
        return categoryMap[category] || category;
      }
    },
    {
      title: '条件',
      key: 'condition',
      width: 150,
      render: (_: any, record: Achievement) => {
        return `${record.condition_type}: ${record.condition_value}`;
      }
    },
    {
      title: 'レアリティ',
      dataIndex: 'rarity',
      key: 'rarity',
      width: 100,
      render: (rarity: string) => <Tag color={getRarityColor(rarity)}>{rarity}</Tag>
    },
    {
      title: '報酬PT',
      dataIndex: 'reward_points',
      key: 'reward_points',
      width: 80,
      render: (points: number) => `${points}pt`
    },
    {
      title: '状態',
      key: 'status',
      width: 120,
      render: (_: any, record: Achievement) => (
        <Space size="small">
          <Switch
            checked={record.is_active}
            onChange={(checked) => handleToggleActive(record.id, checked)}
            checkedChildren="有効"
            unCheckedChildren="無効"
          />
          {record.is_hidden && <Tag color="gray">隠し</Tag>}
        </Space>
      )
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      fixed: 'right' as const,
      render: (_: any, record: Achievement) => (
        <Space size="small">
          <Tooltip title="編集">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="この称号を削除してもよろしいですか？"
            onConfirm={() => handleDelete(record.id)}
            okText="削除"
            cancelText="キャンセル"
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  if (!isUnlocked) {
    return (
      <div style={{ padding: '24px' }}>
        {/* プレミアムオプション案内 */}
        <div style={{
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          borderRadius: 16,
          padding: '48px 32px',
          color: '#fff',
          textAlign: 'center',
          marginBottom: 24,
          boxShadow: '0 20px 60px rgba(245, 87, 108, 0.3)'
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>&#x1F3C6;</div>
          <h1 style={{ fontSize: 28, fontWeight: 800, margin: '0 0 8px 0', letterSpacing: 1 }}>
            プレミアムオプション
          </h1>
          <h2 style={{ fontSize: 20, fontWeight: 400, margin: '0 0 32px 0', opacity: 0.9 }}>
            称号マスタ管理
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 16,
            maxWidth: 800,
            margin: '0 auto 32px',
            textAlign: 'left'
          }}>
            {[
              { icon: '\u{1F3C5}', title: '称号一覧管理', desc: '全称号の有効/無効・レアリティ・条件を一覧管理' },
              { icon: '\u{270F}\u{FE0F}', title: '称号編集・作成', desc: '新しい称号の作成、既存称号の条件・報酬ポイント変更' },
              { icon: '\u{1F50D}', title: 'フィルタリング', desc: 'カテゴリ別・レアリティ別に絞り込み表示' },
              { icon: '\u{1F381}', title: '報酬設定', desc: '称号達成時の報酬ポイント・クーポン設定' },
            ].map((feature, i) => (
              <div key={i} style={{
                background: 'rgba(255,255,255,0.15)',
                borderRadius: 12,
                padding: '16px 20px',
                backdropFilter: 'blur(10px)'
              }}>
                <div style={{ fontSize: 24, marginBottom: 4 }}>{feature.icon}</div>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{feature.title}</div>
                <div style={{ fontSize: 12, opacity: 0.85 }}>{feature.desc}</div>
              </div>
            ))}
          </div>

          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            background: 'rgba(255,255,255,0.2)',
            borderRadius: 24,
            padding: '10px 24px',
            fontSize: 14,
            fontWeight: 600
          }}>
            <LockOutlined />
            <span>プレミアムオプション（有償）</span>
          </div>
        </div>

        {/* エラー報告・検収案内 */}
        <Card style={{ marginBottom: 24, borderColor: '#faad14' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 12, color: '#d48806' }}>
              &#x26A0;&#xFE0F; 一部エラーが確認されています
            </div>
            <p style={{ color: '#595959', fontSize: 14, lineHeight: 1.8, marginBottom: 16 }}>
              本機能につきまして、一部動作上のエラーが確認されております。<br />
              称号設定の変更をご希望の場合は管理者までご連絡ください。
            </p>
            <div style={{
              background: '#f6ffed',
              border: '1px solid #b7eb8f',
              borderRadius: 8,
              padding: '16px 24px',
              textAlign: 'left',
              maxWidth: 500,
              margin: '0 auto'
            }}>
              <p style={{ fontWeight: 700, color: '#389e0d', marginBottom: 8, fontSize: 14 }}>
                &#x1F4C5; 検収前初期設定期間
              </p>
              <p style={{ color: '#595959', fontSize: 13, margin: 0, lineHeight: 1.8 }}>
                <strong>2月25日まで</strong>：検収前初期設定期間につき、称号設定の変更は<strong style={{ color: '#389e0d' }}>無償</strong>にて対応いたします。<br />
                <strong>2月26日以降</strong>：別途お見積りさせていただきます。
              </p>
            </div>
          </div>
        </Card>

        {/* ぼかしプレビュー */}
        <div style={{ filter: 'blur(4px)', opacity: 0.5, pointerEvents: 'none' }}>
          <Card
            title={<Space><TrophyOutlined /><span>称号マスタ管理</span><Badge count={25} style={{ backgroundColor: '#52c41a' }} /></Space>}
          >
            <Table
              columns={[
                { title: '並び順', dataIndex: 'sort_order', width: 80 },
                { title: 'アイコン', dataIndex: 'icon', width: 60 },
                { title: 'コード', dataIndex: 'code', width: 150 },
                { title: '称号名', dataIndex: 'name', width: 150 },
                { title: '説明', dataIndex: 'description' },
                { title: 'カテゴリ', dataIndex: 'category', width: 100 },
                { title: 'レアリティ', dataIndex: 'rarity', width: 100 },
              ]}
              dataSource={[
                { key: 1, sort_order: 1, icon: '🔰', code: 'first_visit', name: '初来店', description: '初めて来店', category: '来店', rarity: 'common' },
                { key: 2, sort_order: 2, icon: '⭐', code: 'regular', name: '常連さん', description: '10回来店', category: '来店', rarity: 'rare' },
                { key: 3, sort_order: 3, icon: '👑', code: 'vip', name: 'VIP', description: '50回来店', category: '来店', rarity: 'legendary' },
              ]}
              pagination={false}
            />
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={
          <Space>
            <TrophyOutlined />
            <span>称号マスタ管理</span>
            <Badge count={achievements.length} style={{ backgroundColor: '#52c41a' }} />
          </Space>
        }
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchAchievements}>
              更新
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
              新規作成
            </Button>
          </Space>
        }
      >
        {/* フィルター */}
        <div style={{ marginBottom: 16 }}>
          <Space>
            <FilterOutlined />
            <Select
              value={filterCategory}
              onChange={setFilterCategory}
              style={{ width: 150 }}
              options={categoryOptions}
            />
            <Select
              value={filterRarity}
              onChange={setFilterRarity}
              style={{ width: 150 }}
              options={rarityOptions}
            />
          </Space>
        </div>

        {/* テーブル */}
        <Table
          columns={columns}
          dataSource={filteredAchievements}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `全 ${total} 件`
          }}
          scroll={{ x: 1400 }}
        />
      </Card>

      {/* 編集/作成モーダル */}
      <Modal
        title={editingAchievement ? '称号編集' : '称号新規作成'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingAchievement(null);
        }}
        onOk={() => form.submit()}
        okText="保存"
        cancelText="キャンセル"
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            icon: '⭐',
            category: 'special',
            rarity: 'common',
            sort_order: 999,
            is_active: true,
            is_hidden: false,
            reward_points: 0
          }}
        >
          <Form.Item
            label="称号コード"
            name="code"
            rules={[
              { required: true, message: '称号コードを入力してください' },
              { pattern: /^[a-z0-9_]+$/, message: '英数字とアンダースコアのみ使用できます' }
            ]}
          >
            <Input placeholder="first_visit" disabled={!!editingAchievement} />
          </Form.Item>

          <Form.Item
            label="称号名"
            name="name"
            rules={[{ required: true, message: '称号名を入力してください' }]}
          >
            <Input placeholder="初来店マスター" />
          </Form.Item>

          <Form.Item label="説明" name="description">
            <TextArea rows={3} placeholder="初めて来店した際に獲得" />
          </Form.Item>

          <Space style={{ width: '100%' }} size="large">
            <Form.Item label="アイコン" name="icon" style={{ marginBottom: 0 }}>
              <Select style={{ width: 100 }} showSearch placeholder="絵文字選択">
                <Option value="🎉">🎉 祝</Option>
                <Option value="🎊">🎊 紙吹雪</Option>
                <Option value="⭐">⭐ 星</Option>
                <Option value="🌟">🌟 輝星</Option>
                <Option value="💫">💫 流星</Option>
                <Option value="✨">✨ キラキラ</Option>
                <Option value="🏆">🏆 トロフィー</Option>
                <Option value="🥇">🥇 金メダル</Option>
                <Option value="🥈">🥈 銀メダル</Option>
                <Option value="🥉">🥉 銅メダル</Option>
                <Option value="👑">👑 王冠</Option>
                <Option value="💎">💎 ダイヤ</Option>
                <Option value="💰">💰 お金</Option>
                <Option value="🔥">🔥 炎</Option>
                <Option value="⚡">⚡ 雷</Option>
                <Option value="🏅">🏅 メダル</Option>
                <Option value="🎖️">🎖️ 勲章</Option>
                <Option value="🏪">🏪 店</Option>
                <Option value="🍴">🍴 フォーク</Option>
                <Option value="🍽️">🍽️ 食器</Option>
                <Option value="🐷">🐷 豚</Option>
                <Option value="🥩">🥩 肉</Option>
                <Option value="🍖">🍖 骨付き肉</Option>
                <Option value="🎂">🎂 ケーキ</Option>
                <Option value="🎁">🎁 プレゼント</Option>
                <Option value="🎈">🎈 風船</Option>
                <Option value="🎯">🎯 的</Option>
                <Option value="🎮">🎮 ゲーム</Option>
                <Option value="📅">📅 カレンダー</Option>
                <Option value="⏰">⏰ 時計</Option>
                <Option value="💪">💪 筋肉</Option>
                <Option value="❤️">❤️ ハート</Option>
                <Option value="🔰">🔰 初心者</Option>
              </Select>
            </Form.Item>

            <Form.Item label="カテゴリ" name="category" style={{ marginBottom: 0 }}>
              <Select style={{ width: 150 }}>
                <Option value="visit">🏪 来店系</Option>
                <Option value="purchase">💰 購入系</Option>
                <Option value="point">💎 ポイント系</Option>
                <Option value="menu">🍴 メニュー系</Option>
                <Option value="special">⭐ 特別系</Option>
              </Select>
            </Form.Item>

            <Form.Item label="レアリティ" name="rarity" style={{ marginBottom: 0 }}>
              <Select style={{ width: 130 }}>
                <Option value="common">コモン</Option>
                <Option value="rare">レア</Option>
                <Option value="epic">エピック</Option>
                <Option value="legendary">レジェンダリー</Option>
              </Select>
            </Form.Item>
          </Space>

          <Space style={{ width: '100%' }} size="large">
            <Form.Item
              label="条件タイプ"
              name="condition_type"
              rules={[{ required: true }]}
              style={{ marginBottom: 0 }}
            >
              <Select style={{ width: 220 }}>
                <Option value="visit_count">📍 来店回数</Option>
                <Option value="monthly_visit">📅 月間来店数</Option>
                <Option value="lunch_visit">🍱 ランチタイム来店</Option>
                <Option value="birthday_visit">🎂 誕生日来店</Option>
                <Option value="early_visit">🌅 早朝来店</Option>
                <Option value="late_visit">🌙 深夜来店</Option>
                <Option value="total_purchase">💰 累計購入額</Option>
                <Option value="point_total">💎 ポイント合計</Option>
                <Option value="menu_variety">🍽️ メニュー種類数</Option>
                <Option value="menu_complete">🏆 メニュー達成率</Option>
                <Option value="challenge_clear">🎯 チャレンジクリア</Option>
                <Option value="manual">✋ 手動付与</Option>
              </Select>
            </Form.Item>

            <Form.Item
              label="条件値"
              name="condition_value"
              rules={[{ required: true }]}
              style={{ marginBottom: 0 }}
            >
              <InputNumber min={0} style={{ width: 100 }} />
            </Form.Item>

            <Form.Item label="表示順" name="sort_order" style={{ marginBottom: 0 }}>
              <InputNumber min={0} style={{ width: 100 }} />
            </Form.Item>
          </Space>

          <Form.Item label="報酬ポイント" name="reward_points">
            <InputNumber min={0} style={{ width: 120 }} />
          </Form.Item>

          <Form.Item label="報酬クーポンコード" name="reward_coupon_code">
            <Input placeholder="クーポンコード（任意）" />
          </Form.Item>

          <Space>
            <Form.Item name="is_active" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Switch checkedChildren="有効" unCheckedChildren="無効" />
            </Form.Item>

            <Form.Item name="is_hidden" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Switch checkedChildren="隠し称号" unCheckedChildren="通常" />
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </div>
  );
};

export default AchievementMasterList;
